import React, { useEffect, useMemo, useState } from "react";
import { FormGroup, Label, Input, Button } from "reactstrap";
import http from "../../utils/http-common";

const ORIGIN_DECLARED = "declared";
const DECLARED_TYPE = "DECLARED";
const DISCLAIMER_PLACEHOLDER =
  "[Pending legal review] Practitioner-declared mentions appear on prescriptions; the practitioner remains responsible for their accuracy.";

/**
 * Admin dynamic license editors from PrescriptionRegulation.licenseTypes
 * + practitioner-declared mentions (preserved on save).
 *
 * @param {{
 *   practiceCountry?: string | null,
 *   licenses?: { type?: string, number?: string, origin?: string, label?: string }[],
 *   professionalId?: string | null,
 *   medicalLicenceNumber?: string | null,
 *   disabled?: boolean,
 *   onSave: (licenses: object[]) => void | Promise<void>,
 * }} props
 */
export default function LicenseEditors({
  practiceCountry = null,
  licenses = [],
  professionalId = null,
  medicalLicenceNumber = null,
  disabled = false,
  onSave,
}) {
  const country = practiceCountry ? String(practiceCountry).trim().toUpperCase() : "";
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [draft, setDraft] = useState({});
  const [declared, setDeclared] = useState([]);

  useEffect(() => {
    if (!country || country.length !== 2) {
      setTypes([]);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    http
      .get(`/api/v1/prescription-regulations/${country}`, { suppressInlineErrorSwal: true })
      .then((res) => {
        if (cancelled) return;
        const rows = Array.isArray(res.data?.data?.licenseTypes) ? res.data.data.licenseTypes : [];
        setTypes(rows);
      })
      .catch(() => {
        if (!cancelled) setTypes([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [country]);

  const seed = useMemo(() => {
    const map = {};
    const declaredRows = [];
    (Array.isArray(licenses) ? licenses : []).forEach((row) => {
      const origin = String(row?.origin || "").toLowerCase();
      const type = String(row?.type || "")
        .trim()
        .toUpperCase();
      const number = String(row?.number || "").trim();
      if (origin === ORIGIN_DECLARED || type === DECLARED_TYPE) {
        if (number || row?.label) {
          declaredRows.push({
            type: DECLARED_TYPE,
            origin: ORIGIN_DECLARED,
            label: String(row?.label || "").trim(),
            number,
          });
        }
        return;
      }
      if (type && number) map[type] = number;
    });
    if (!Object.keys(map).length) {
      const legacy = String(professionalId || medicalLicenceNumber || "").trim();
      const primary = types[0]?.code ? String(types[0].code).toUpperCase() : "";
      if (legacy && primary) map[primary] = legacy;
    }
    return { map, declaredRows };
  }, [licenses, professionalId, medicalLicenceNumber, types]);

  useEffect(() => {
    const next = {};
    types.forEach((row) => {
      const code = String(row?.code || "")
        .trim()
        .toUpperCase();
      if (!code) return;
      next[code] = seed.map[code] || "";
    });
    setDraft(next);
    setDeclared(seed.declaredRows);
  }, [types, seed]);

  const persist = (nextDraft, nextDeclared) => {
    const regulation = Object.entries(nextDraft)
      .map(([type, number]) => ({
        type: String(type).trim().toUpperCase(),
        number: String(number || "").trim(),
        origin: "regulation",
      }))
      .filter((item) => item.type && item.number);
    const declaredPayload = (nextDeclared || [])
      .map((row) => ({
        type: DECLARED_TYPE,
        origin: ORIGIN_DECLARED,
        label: String(row.label || "").trim(),
        number: String(row.number || "").trim(),
      }))
      .filter((row) => row.label && row.number);
    onSave([...regulation, ...declaredPayload]);
  };

  if (!country) {
    return <span className="text-soft small">Set practice country to configure licenses.</span>;
  }
  if (loading && !types.length) {
    return <span className="text-soft small">Loading…</span>;
  }
  if (!types.length) {
    return <span className="text-soft small">No licence types for {country}.</span>;
  }

  const required = types.filter((row) => row.required === true);
  const optional = types.filter((row) => row.required !== true);

  return (
    <div>
      {required.length > 0 && (
        <div className="mb-2">
          <div className="small text-soft mb-1">Required</div>
          {required.map((row) => {
            const code = String(row.code || "")
              .trim()
              .toUpperCase();
            if (!code) return null;
            const label = row.label || code;
            return (
              <FormGroup key={code} className="mb-1">
                <Label className="small mb-0">
                  {label} *
                </Label>
                <Input
                  type="text"
                  className="form-control-sm"
                  value={draft[code] || ""}
                  disabled={disabled}
                  maxLength={64}
                  onChange={(e) => setDraft((prev) => ({ ...prev, [code]: e.target.value }))}
                  onBlur={(e) => {
                    const next = { ...draft, [code]: e.target.value };
                    setDraft(next);
                    persist(next, declared);
                  }}
                  aria-label={label}
                />
              </FormGroup>
            );
          })}
        </div>
      )}

      {optional.length > 0 && (
        <div className="mb-2">
          <div className="small text-soft mb-1">Optional (admin types)</div>
          {optional.map((row) => {
            const code = String(row.code || "")
              .trim()
              .toUpperCase();
            if (!code) return null;
            const label = row.label || code;
            return (
              <FormGroup key={code} className="mb-1">
                <Label className="small mb-0">{label}</Label>
                <Input
                  type="text"
                  className="form-control-sm"
                  value={draft[code] || ""}
                  disabled={disabled}
                  maxLength={64}
                  onChange={(e) => setDraft((prev) => ({ ...prev, [code]: e.target.value }))}
                  onBlur={(e) => {
                    const next = { ...draft, [code]: e.target.value };
                    setDraft(next);
                    persist(next, declared);
                  }}
                  aria-label={label}
                />
              </FormGroup>
            );
          })}
        </div>
      )}

      <div className="mb-1">
        <div className="d-flex justify-content-between align-items-center mb-1">
          <div className="small text-soft mb-0">Declared by practitioner</div>
          {!disabled && (
            <Button
              color="link"
              size="sm"
              className="p-0"
              type="button"
              onClick={() => {
                const next = [...declared, { label: "", number: "", origin: ORIGIN_DECLARED, type: DECLARED_TYPE }];
                setDeclared(next);
              }}
            >
              Add
            </Button>
          )}
        </div>
        <p className="small text-soft mb-2" style={{ lineHeight: 1.4 }}>
          {DISCLAIMER_PLACEHOLDER}
        </p>
        {declared.length === 0 ? (
          <span className="text-soft small">None</span>
        ) : (
          declared.map((row, index) => (
            <div key={`decl-${index}`} className="mb-2">
              <Input
                type="text"
                className="form-control-sm mb-1"
                placeholder="Label"
                value={row.label || ""}
                disabled={disabled}
                maxLength={120}
                onChange={(e) => {
                  const next = declared.map((r, i) => (i === index ? { ...r, label: e.target.value } : r));
                  setDeclared(next);
                }}
                onBlur={() => persist(draft, declared)}
              />
              <div className="d-flex gap-1">
                <Input
                  type="text"
                  className="form-control-sm"
                  placeholder="Number"
                  value={row.number || ""}
                  disabled={disabled}
                  maxLength={64}
                  onChange={(e) => {
                    const next = declared.map((r, i) => (i === index ? { ...r, number: e.target.value } : r));
                    setDeclared(next);
                  }}
                  onBlur={() => persist(draft, declared)}
                />
                {!disabled && (
                  <Button
                    color="light"
                    size="sm"
                    type="button"
                    onClick={() => {
                      const next = declared.filter((_, i) => i !== index);
                      setDeclared(next);
                      persist(draft, next);
                    }}
                  >
                    ×
                  </Button>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
